import { hashKey } from '@tanstack/react-query';
import { isNil } from 'lodash';

/* Imported past the `@/utils` barrel, so a consumer of this module does not pull in mapbox-gl with it. */
import { LIVE_WINDOW_MS } from '@/utils/liveRange';
import type { DataWindow } from '@/utils/seriesWindows';

declare const placeholderCacheKeyBrand: unique symbol;

/**
 * Names one series for one spotter.
 *
 * Every input that changes what a window's rows are, or what order they come in, has to reach
 * `querySeriesIdentity`: the endpoint, the request params, whether the series is public, and both the
 * field it is cut on and the order it is sorted in. A key that misses one of those reads back a window
 * assembled from a different series.
 *
 * The view token is not among them, and neither is it in the query key a window is cached under. A token
 * decides which spotters a session may read, not what a spotter's rows are, so two public sessions on
 * different tokens that both reach one spotter are looking at the same series.
 *
 * Branded, so only {@link placeholderCacheKey} can mint one and every key in the cache is built from that
 * whole identity.
 */
export type PlaceholderCacheKey = string & { readonly [placeholderCacheKeyBrand]: true };

/**
 * Mints the key `spotterId`'s series is held under within `querySeriesIdentity`.
 *
 * The brand cannot be expressed without the assertion, and this is its single choke point: a caller
 * reaches a key only by naming both halves of the identity here.
 */
export const placeholderCacheKey = (spotterId: string, querySeriesIdentity: unknown[]): PlaceholderCacheKey =>
    hashKey([...querySeriesIdentity, spotterId]) as PlaceholderCacheKey;

/**
 * How many assembled windows are held for a later range to fall back on.
 *
 * Each one keeps its rows alive past the query cache, so this is what bounds that. It is well above the
 * windows one page is looking at, and the map viewport moving across many spotters is what it is for.
 */
export const PLACEHOLDER_CACHE_LIMIT = 32;

/**
 * Whether enough of `window` lands inside `[startMs, endMs]` to be worth drawing while that range's own
 * windows are in flight.
 *
 * Nothing held has to stay inside the range being asked for, because the caller clips what it hands out,
 * so all that is left to ask is how much of the two overlap. One live window is the floor, or the whole
 * span for a range narrower than that, which is what stops two ranges meeting end to end answering each
 * other with an all but empty series.
 */
const isWorthShowing = (window: DataWindow<never>, startMs: number, endMs: number): boolean =>
    Math.min(window.endMs, endMs) - Math.max(window.startMs, startMs) >= Math.min(LIVE_WINDOW_MS, endMs - startMs);

/**
 * Holds the window assembled last for each series and spotter, so a range change draws that window while
 * the new one is in flight and a section reopening finds what it was showing before.
 *
 * This sits alongside the query cache because neither of react-query's own mechanisms reaches these rows.
 * `placeholderData` reads the previous data of the same query key, and every key here carries the range,
 * so a range change has no previous data to read. `gcTime` promises nothing once a spotter that scrolled
 * out of the map viewport unmounts, and these rows have to outlive that.
 *
 * Entries are held in least-recently-remembered order and evicted at {@link PLACEHOLDER_CACHE_LIMIT}.
 */
export class PlaceholderCache {
    private readonly windows = new Map<PlaceholderCacheKey, DataWindow<never>>();

    /** Holds `window` as the answer a later range under `key` can fall back on, evicting the oldest. */
    remember<TItem>(key: PlaceholderCacheKey, window: DataWindow<TItem>): void {
        // re-inserting moves the key to the end, so the map stays in least-recently-remembered order
        this.windows.delete(key);
        /*
         * The map spans every caller's item type, which no single type parameter can describe. A
         * `PlaceholderCacheKey` names the endpoint, the request params and the spotter, so what is read
         * back under one is the series that wrote it.
         */
        this.windows.set(key, window as DataWindow<never>);

        if (this.windows.size > PLACEHOLDER_CACHE_LIMIT) {
            const oldest = this.windows.keys().next();
            if (!oldest.done) {
                this.windows.delete(oldest.value);
            }
        }
    }

    /**
     * The window assembled last under `key`, when enough of it overlaps `[startMs, endMs]` to stand in for
     * that range's answer.
     */
    forRange<TItem>(
        key: PlaceholderCacheKey,
        startMs: number | undefined,
        endMs: number | undefined
    ): DataWindow<TItem> | undefined {
        const held = this.windows.get(key);
        if (isNil(held) || isNil(startMs) || isNil(endMs) || !isWorthShowing(held, startMs, endMs)) {
            return undefined;
        }
        return held;
    }

    /** Drops every held window, so a test starts from nothing. */
    reset(): void {
        this.windows.clear();
    }

    /** How many windows are held, so a test can assert on the eviction. */
    get size(): number {
        return this.windows.size;
    }
}

/**
 * Shared rather than per hook, so the ten hooks over one series hold one window between them, and so a
 * freshly mounted one starts from what its neighbours already assembled.
 */
export const placeholderCache = new PlaceholderCache();
